// src/pages/BusinessRegistrationPage.js
import React, { useState } from 'react';
import { SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Step1BasicInfo from "../../components/businessRegister/Step1BasicInfo";
import Step2ContactLocation from "../../components/businessRegister/Step2ContactLocation";
import Step3LocalizationOps from "../../components/businessRegister/Step3LocalizationOps";
import {db} from "../../firebase";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";

const BusinessRegistrationPage = () => {
    const {userId} = useParams();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Step 1
        businessName: '',
        businessType: '',
        industry: '',
        // Step 2
        streetAddress: '',
        city: '',
        stateProvince: '',
        postalCode: '',
        country: 'South Africa', // Default based on current context
        businessPhone: '',
        businessWebsite: '',
        // Step 3
        primaryCurrency: 'ZAR', // Default based on current context
        timezone: 'Africa/Johannesburg', // Default based on current context
        numberOfLocations: '1',
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Total steps for the progress indicator
    const totalSteps = 3;

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const validateStep = () => {
        setError(''); // Clear previous errors
        switch (currentStep) {
            case 1:
                if (!formData.businessName || !formData.businessType) {
                    setError('Please fill in all required fields for Basic Information.');
                    return false;
                }
                break;
            case 2:
                if (!formData.streetAddress || !formData.city || !formData.postalCode || !formData.country || !formData.businessPhone) {
                    setError('Please fill in all required fields for Contact & Location.');
                    return false;
                }
                // Basic phone number validation (optional, can be more robust)
                if (!/^\+?\d{10,15}$/.test(formData.businessPhone)) {
                    setError('Please enter a valid business phone number (e.g., +27 12 345 6789).');
                    return false;
                }
                break;
            case 3:
                if (!formData.primaryCurrency || !formData.timezone || !formData.numberOfLocations) {
                    setError('Please fill in all required fields for Localization & Operations.');
                    return false;
                }
                break;
            default:
                return true;
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            if (currentStep < totalSteps) {
                setCurrentStep(prev => prev + 1);
            } else {
                // This is the last step, so perform final submission
                handleRegisterBusiness();
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
            setError(''); // Clear error when going back
        }
    };

    const handleRegisterBusiness = async () => {
        setLoading(true);

        const postId = db.ref().push().key

        db.ref().child(`localRewards/businessProfile/${userId}`).update({
            businessName: formData.businessName,
            businessType: formData.businessType,
            businessPhone: formData.businessPhone,
            businessWebsite: formData?.businessWebsite,
            primaryCurrency: formData.primaryCurrency,
            timezone: formData.timezone,
            numberOfLocations: formData.numberOfLocations,
            industry: formData.industry,
            streetAddress: formData.streetAddress,
            city: formData.city,
            stateProvince: formData.city,
            postalCode: formData.postalCode,
            country: formData.country,
            createdOn: new Date().getTime(),
            userId: userId,
            postId: postId,
            isVerified: false
        }).then(() => {
            toast.success(`Business "${formData.businessName}" registered successfully! Welcome to LocalRewards.`)
            setLoading(false)
            navigate(`/register-program/${userId}/${postId}`)
        }).catch((err) => {
            toast.error(err.message);
        })
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1BasicInfo formData={formData} updateFormData={updateFormData} />;
            case 2:
                return <Step2ContactLocation formData={formData} updateFormData={updateFormData} />;
            case 3:
                return <Step3LocalizationOps formData={formData} updateFormData={updateFormData} />;
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-xl w-full bg-white rounded-xl shadow-2xl p-8 space-y-8 animate-fade-in-up">
                <div>
                    {/* LocalRewards Logo */}
                    <div className="flex justify-center mb-6">
                        <SparklesIcon className="h-16 w-16 text-indigo-600" />
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Register Your Business
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Tell us about your business to get started with LocalRewards.
                    </p>
                </div>

                {/* Progress Indicator */}
                <div className="flex justify-between items-center mb-6">
                    {[...Array(totalSteps)].map((_, index) => (
                        <div key={index} className="flex-1 flex flex-col items-center">
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    currentStep >= index + 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
                                }`}
                            >
                                {index + 1}
                            </div>
                            {index < totalSteps - 1 && (
                                <div className={`flex-1 h-1 ${
                                    currentStep > index + 1 ? 'bg-indigo-600' : 'bg-gray-200'
                                } mx-2 absolute w-1/3 left-1/3 -z-10`} style={{ left: `calc(${33.33 * index}% + 2rem)`, width: `calc(${33.33}% - 4rem)` }}>
                                    {/* Visual connector line between steps */}
                                </div>
                            )}
                        </div>
                    ))}
                </div>


                <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative" role="alert">
                            <strong className="font-bold">Error:</strong>
                            <span className="block sm:inline ml-2">{error}</span>
                        </div>
                    )}

                    {renderStep()}

                    <div className="flex justify-between pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={currentStep === 1 || loading}
                            className={`inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white ${
                                currentStep === 1 || loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            }`}
                        >
                            Back
                        </button>
                        <button
                            type="button" // Use type="button" to prevent default form submission
                            onClick={handleNext}
                            className="group relative inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                            disabled={loading}
                        >
                            {currentStep === totalSteps ? (loading ? 'Registering...' : 'Complete Registration') : 'Next'}
                            {loading ? (
                                <svg className="animate-spin -mr-1 ml-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <ArrowRightIcon className="-mr-1 ml-2 h-5 w-5 text-indigo-300 group-hover:text-white" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </form>

                {/* Option to skip or go back if part of a flow */}
                {/*<div className="mt-6 text-center">*/}
                {/*    <p className="text-sm text-gray-600">*/}
                {/*        <a href="/dashboard" className="font-medium text-indigo-600 hover:text-indigo-500">*/}
                {/*            Skip for now*/}
                {/*        </a>{' '}*/}
                {/*        (limited functionality)*/}
                {/*    </p>*/}
                {/*</div>*/}
            </div>
        </div>
    );
};

export default BusinessRegistrationPage;